import React from "react"
import { HeroImage } from "../components/image"
import { Figure5 } from "../components/Figure5"
import {
  TwoWheelerSalesData,
  carSalesData,
  homeLoansGrowthFall,
  yearlyReturnsRBIHousePriceIndex,
  nonBankingFinCompanies,
} from "../data"
import { H4 } from "../components/H4"
import { A } from "../components/A"
import { Table } from "../components/Table"
import { Source } from "../components/Source"
import { Header } from "../components/Header"
import {
  CustomRangeLineChart,
  CustomTestLineChart,
} from "../components/DrawLine"
import { ShareButtons } from "../components/ShareButtons"
import { SubscribeBottom } from "../components/SubsribeBottom"
import { FigureText } from "../components/FigureText"
import { MeTypeComponent } from "../components/Metype"
import { Image1 } from "../components/Illustrations/Image1"
import { Image2 } from "../components/Illustrations/Image2"
import { Image3 } from "../components/Illustrations/Image3"
import { Image4 } from "../components/Illustrations/Image4"
import { Image5 } from "../components/Illustrations/Image5"
import { Image6 } from "../components/Illustrations/Image6"
import { Image7 } from "../components/Illustrations/Image7"
import { Metadata } from "../components/PageMetadata"

const IndexPage = () => {
  return (
    <div>
      <Metadata />
      <Header />
      <div className="hero-wrapper">
        <div className="flex items-center content-center title-wrapper text-center xl:mb-40">
          <div className="pt-5 px-5 sm:px-10 md:px-20">
            <a href="https://newslaundry.com/nl-sena" target="__blank">
              <h2 className="font-bold section-name mb-2 text-lg">NL SENA</h2>
            </a>
            <h1 className="text-2xl md:text-4xl font-bold">
              India’s unreal estate: Why you still can’t afford to buy that home
            </h1>
            <h6 className="italic mb-2">
              Are you wondering if home prices will fall in the post-Covid
              world? There’s something you should know: the real estate industry
              in India doesn’t want this to happen.
            </h6>
            <div className="font-bold font-lora text-gray-800 text-base text-left mt-10">
              By{" "}
              <a
                className="underline"
                target="__blank"
                href="https://www.newslaundry.com/author/vivek-kaul"
              >
                Vivek Kaul
              </a>
            </div>
            <div className="text-gray-700 text-sm flex justify-between items-center">
              <div>Published Jul 21, 2020</div>
              <div>
                <ShareButtons />
              </div>
            </div>
          </div>
        </div>
        <div>
          <HeroImage />
          {/* <ShareButtons /> */}
        </div>
      </div>

      <div className="wrapper pt-10 md:pt-15 md:mt-5">
        <div className="text-wrapper">
          <p>
            <strong className="text-3xl">T</strong>he film director Basu
            Chatterjee who recently died was known for making slice-of-life
            cinema. Among his lesser known movies is a movie called{" "}
            <span className="italic">Kirayadar </span> (tenant) which was
            released in 1986. Showing the travails of renting a house in a big
            city like Mumbai, it’s one of the few Hindi movies based on the
            story of a tenant and the struggle with the landlord. Also, the
            title song of the movie, more than the movie itself, is a wonderful
            summary of all the problems that a tenant has to face.
          </p>
          <p>
            In fact, from putting up a huge security deposit to dealing with
            huge insecurities and egos of landlords (simply because they happen
            to own a house and nothing more), and neighbours who don’t treat you
            well because you are a tenant, tenants in India have to go through a
            lot. On top of all this is the massive pressure from parents and
            relatives to get “settled” in life by buying a house.
          </p>
          <p>
            Of course, there is some flip side to this as well, with tenants
            making life difficult for landlords by not moving out once the
            contract ends or creating a general nuisance.
          </p>
          <p>
            The trouble is that in most Indian cities, home prices went up at a
            massive pace between 2002 and 2015. And even though they have fallen
            in a few places and remained stable in others, home prices still
            remain unaffordable for the average Indian.
          </p>
          <p>
            The question I have often been asked over the last decade is, when
            will home prices fall? If one goes by basic economics and the law of
            demand, home prices should have fallen dramatically by now. But they
            haven’t. The reason for this, as we shall see, is that real estate
            in India has become a low supply, low demand market (yes, low demand
            — you read that right).
          </p>
          <p>
            How will things play out in the post-Covid environment? There is
            bound to be some fall in home prices. But will that be enough to get
            buyers back into the market? Not immediately, simply because the
            fall in prices won’t be enough to draw in buyers, especially at a
            time when people are losing jobs, incomes are falling or stagnating,
            and many small-businesses are collapsing.
          </p>
          <p>
            And more than anything, there is a whole system out there, the deep
            state of real estate in India, which does not want home prices to
            fall.
          </p>
        </div>

        <div className="text-wrapper">
          <H4>The great Indian slowdown</H4>
          <p>
            Biscuits sold well under the Lockdown. Parle G, one of the most
            popular biscuit brands in the country, achieved its
            <A href="https://timesofindia.indiatimes.com/business/india-business/biscuits-sell-like-hot-cakes-amid-lockdown/articleshow/76293399.cms">
              highest sales volumes in eight decades
            </A>
            between March to May 2020.
          </p>
          <p>
            It seems biscuits were very popular with the thousands of migrant
            labourers who walked to their homes from India’s biggest cities.
            Those stuck at home, thanks to the lockdown, and looking to snack
            also ate more biscuits than they normally would.
          </p>
          <p>
            In fact, biscuit sales might just prove to be the exception to the
            rule when it comes to businesses and the economy this year. The
            rating agency Crisil
            <A href="https://timesofindia.indiatimes.com/business/india-business/fmcg-sector-to-de-grow-2-3-this-fiscal-crisil/articleshow/76173163.cms">
              expects the total revenues
            </A>
            of the fast-moving consumer goods, or FMCG, sector to fall 2-3
            percent (or what the analysts like to call “de-grow”) during this
            financial year. Before Covid-19 struck, the revenues of these
            companies were expected to grow by 8-10 percent.
          </p>
        </div>
        <Image2 caption={""} alt="India's real estate - migrants artwork" />
        <div className="text-wrapper">
          <p>
            FMCG companies produce products of everyday use, everything from
            soaps to detergents to toothpaste to shampoo to packaged food to
            beverages. Typically, the sales of these companies are expected to
            be the most stable of the lot. The reason for this is really simple;
            come what may, people will continue to buy products of everyday
            consumption. This logic stems from the fact that people will bathe
            regularly, wash clothes regularly, brush their teeth regularly, eat
            regularly, and so on.
          </p>
          <p>
            But Crisil expects the sales of these products of everyday use to
            contract this year. One of the major reasons for this is the fact
            that the incomes of many individuals are expected to fall during the
            course of this year. In an environment of falling income, people
            look to cut down on even their everyday expenses.
          </p>
        </div>
        <div className="text-wrapper">
          <p>
            When people are cutting down (or not increasing, even) their
            everyday expenditure, it means that things are rather desperate on
            the income front. In this environment, expecting them to buy what
            economists like to call “discretionary products” is rather low.
            Discretionary spending includes money spent by people on everything
            beyond food, toiletries, clothes, fuel, etc. This includes
            automobiles, electronics, and even homes.
          </p>
          <p>
            When people are not confident about their economic future, as the
            situation is currently, they don’t like buying big-ticket items
            (like an expensive TV or an expensive mobile phone or an air
            conditioner or a washing machine, etc.), or committing to make a
            downpayment out of their savings (in the case of buying a car or a
            home) or committing to repaying an EMI for a period of a few months
            or many years (in the case of buying pretty much anything these
            days).
          </p>
          <p>
            In this context, it is very interesting to see what RC Bhargava, the
            chairman of Maruti Suzuki, the country’s largest car maker,{" "}
            <A href="https://www.business-standard.com/article/companies/challenge-for-us-is-supply-chain-rather-than-demand-maruti-s-r-c-bhargava-120061000063_1.html">
              had to say to Business Standard.
            </A>
            Bhargava was confident that Maruti will be able to sell what it
            produces in the month of June. This confidence comes from the fact
            that the company will end up assembling only 30-40 percent of its
            cars in comparison to what it normally produces in June.
          </p>
          <p>
            The reason for this lies in the fact that the Maruti is basically an
            assembler of cars now and depends totally on its vendors and
            suppliers for everything that goes into the making of a car. And
            until these vendors and suppliers come out of the lockdown-related
            issues that they are facing (from a shortage of labour to a
            breakdown in supply chains), the car maker cannot produce as many
            cars as it used to.
          </p>
          <p>
            Given this, Bhargava remains confident of selling the cars that
            Maruti will produce in June and July. Nevertheless, as Business
            Standard points out: “[Bhargava] however, added he could not predict
            the scenario after July, and whether the current trend was merely
            ‘pent-up demand’ after the lockdown.”
          </p>
          <p>
            While Bhargava hasn’t said anything outright, it doesn’t take rocket
            science to conclude that the June and July sales of cars will
            basically be pent-up demand for the lack of sales between the last
            week of March and the end of May. Let’s take a look at Figure 1
            which basically plots the growth and fall in domestic car sales over
            the last five years.
          </p>
        </div>
        <CustomTestLineChart
          xTooltipLabel={x => `Month: ${x}`}
          yTooltipLabel={y => `Per: ${y}%`}
          data={carSalesData}
          title="Figure 1: The growth and fall in domestic car sales over the last five years."
          yLabel="Grown / Fall in %"
          sourceText="Source: Centre for Monitoring Indian Economy"
        />
        <div className="text-wrapper">
          <p>
            Domestic car sales have fallen every month from November 2018
            onwards, in comparison to a year earlier. The same is also true for
            two-wheelers, with sales having fallen every month from December
            2018 onwards. The growth and fall in two-wheeler sales over the last
            five years is plotted in Figure 2.
          </p>
        </div>
        <CustomTestLineChart
          xTooltipLabel={x => `Month: ${x}`}
          yTooltipLabel={y => `Per: ${y}%`}
          data={TwoWheelerSalesData}
          yLabel="Grown / Fall in %"
          title="Figure 2: The growth and fall in two-wheeler sales over the last five years."
          sourceText="Source: Centre for Monitoring Indian Economy"
        />
        <div className="text-wrapper">
          <p>
            In fact, two-wheeler sales fell by 16 percent and 20 percent
            respectively in January and February 2020. It is worth remembering
            that this was the period before the negative impact of Covid-19
            started spreading through the Indian economy.
          </p>
          <p>
            What this tells us is that the Indian consumer was going slow on
            purchasing both cars and two-wheelers even before Covid-19 struck.
            This will only worsen in the time to come as the negative impact of
            Covid-19 spreads further.
          </p>
          <p>
            India was already going through a period of economic slowdown before
            March 2020. In 2020-21, the overall Indian economy is expected to
            contract in size.
          </p>
        </div>
      </div>

      <div className="text-wrapper">
        <H4>The economic contraction of 2020-2021</H4>
        <p>
          Recently, Shaktikanta Das, the governor of the Reserve Bank of India,
          or RBI, said: “[The] GDP growth in 2020-21 is estimated to remain in
          negative territory.” GDP, or gross domestic product, is a measure of
          the economic size of a country.
        </p>
        <p>
          When Das said that GDP growth will remain in negative territory during
          this financial year, what he meant was that the size of the Indian
          economy will basically contract. It will be smaller than it was in
          2019-20.
        </p>
        <p>
          If Das turns out to be right, as he is more than likely to be, this is
          the first time the Indian economy will contract since 1979-80, when
          the economy had contracted by 5.24 percent. This was the year of the
          second oil shock when, due to a revolution and regime change in Iran,
          oil prices had gone through the roof and impacted economic growth
          negatively all across the world.
        </p>
        <p>
          In fact, in the last six decades, this is only the fifth time that the
          Indian economy will contract during the course of a financial year.
          The other four times were between 1960-61 and 1979-80.
        </p>
        <p>
          While Das did not specify by how much the RBI expects the Indian
          economy to contract in 2020-21, the World Bank expects the Indian
          economy to contract by 3.2 percent. As per the economists at Goldman
          Sachs and Nomura, the Indian economy is likely to contract by five
          percent in 2020-21. Economists at Bernstein expect the Indian economy
          to contract by seven percent. The Organisation for Economic
          Co-operation and Development, or OECD, expects the Indian economy to
          contract by 7.3 percent in 2020-21.
        </p>
        <p>
          Of course, one of the reasons for the contraction in the economy will
          be the fact that Indians will cut down on consumption in 2020-21. This
          includes buying fewer cars and two-wheelers during the course of this
          year, despite a likely bump in monthly demand in June and July (in
          comparison to May).
        </p>
        <p>
          Dear reader, by now you must be wondering why am I talking about cars
          and two-wheelers in a piece which clearly has the word “home” in its
          title. Allow me to explain. I was just trying to establish that the
          Indian consumer will go slow on big-ticket purchasing during the
          course of this year.
        </p>
      </div>

      <Image3 caption={""} alt="India's real estate - downpayment artwork" />
      <div className="text-wrapper">
        <p>
          In a scenario where he is likely to go slow on buying cars and
          two-wheelers, how likely is he to buy a home is a question well worth
          asking. Buying a home would require making a downpayment, taking on a
          home loan (in most cases) and, in some cases, requiring enough money
          to pay out a part of the transaction in black.
        </p>
        <p>
          Take a look at Table 1, which basically has the details of the total
          home loans disbursed by public sector banks and housing finance
          companies.
        </p>
      </div>

      <FigureText text="Table 1: Total home loans disbursed by public sector banks and housing finance companies." />

      <Table
        titles={[
          "Year",
          "Total home loans disbursed (in Rs crore)",
          "Number of units",
          "Average home loan size (in Rs lakh)",
        ]}
        rows={[
          ["2018-19", "4,60,572", "38,92,758", "11.83"],
          ["2017-18", "4,27,963", "54,69,111", "7.83"],
          ["2016-17", "3,21,256", "35,56,171", "9.03"],
        ]}
      />
      <Source text={"Source: Annual reports of National Housing Bank"} />

      <div className="text-wrapper pt-5">
        <p>
          The average home loan size in 2016-17, 2017-18 and 2018-19 was Rs 9.03
          lakh, Rs 7.83 lakh and Rs 11.83 lakh, respectively. For people living
          in cities, the average home loan size might appear to be too small.
          And that’s because of the fact that the bulk of the home loans given
          by public sector banks and housing finance companies are up to Rs 10
          lakh. The trouble is that nothing can be bought in a city with that
          kind of a home loan.
        </p>
        <p>Table 2 lists the details of loans of up to Rs 10 lakh.</p>
      </div>

      <FigureText text="Table 2: Home loans of up to Rs 10 lakh disbursed by public sector banks and housing finance companies." />

      <Table
        titles={[
          "Year",
          "Total home loans disbursed (in Rs crore)",
          "Number of units",
          "Average home loan size (in Rs lakh)",
        ]}
        rows={[
          ["2018-19", "41,793", "14,09,076", "2.97"],
          ["2017-18", "44,661", "21,19,386", "2.11"],
          ["2016-17", "42,991", "13,31,893", "3.23"],
        ]}
      />
      <Source text={"Source: Annual reports of National Housing Bank."} />
      <div className="text-wrapper pt-5">
        <p>
          In 2018-19, the average home loan size for a home loan of less than Rs
          10 lakh was Rs 2.97 lakh.
        </p>
        <p>
          One reason that holds back public sector banks from lending to this
          segment is the especially high rate of default, particularly when it
          concerns home loans of up to Rs 2 lakh. In 2018-19, the rate of
          default for loans of up to Rs 2 lakh stood at 20.1 percent. The rate
          of default for loans between Rs 2 lakh and Rs 5 lakh stood at 3.6
          percent, much lower than the rate of default for loans of up to Rs 2
          lakh, but significantly higher than the overall rate.
        </p>
        <p>
          While home loans of less than Rs 10 lakh form a significant proportion
          of the loans given by public sector banks and housing finance
          companies in numerical terms, in value terms, they aren’t really big.
          In 2018-19, these loans formed around 36 percent of the number of home
          loans given, but in value terms, they formed just nine percent of the
          overall loans.
        </p>
        <p>
          When it comes to actual disbursements, the major part of the
          disbursements (in value terms) was for loans of more than Rs 25 lakh.
          In 2018-19, loans of greater than Rs 25 lakh formed 62 percent of the
          overall home loan disbursements carried out by public sector banks and
          housing finance companies.
        </p>
        <p>
          This is what we will concentrate on in this piece. The average loan
          size of loans greater than Rs 25 lakh in 2018-19 was Rs 28.68 lakh. Of
          course, the data here includes only home loans given out by public
          sector banks and housing finance companies.
        </p>
      </div>

      <div className="text-wrapper">
        <p>
          Over and above this, private banks also give out home loans. The
          National Housing Bank does not take their data into account.
          <A href="https://www.livemint.com/Companies/FXXSNbSwm3oLWlxdqBLUoM/ICICI-Bank-aims-to-grow-home-loan-book-to--2-trillion-by-FY.html">
            A July 2018 newsreport in the Mint
          </A>{" "}
          points out that the average home loan size for ICICI Bank was at Rs 30
          lakh. So, the size of the average home loan given by private banks is
          generally slightly higher than that of public sector banks. But that
          doesn’t really change the logic of what I am about to say.
        </p>
        <p>
          What kind of a home can a borrower hope to buy if he takes on a home
          loan of this size? The median loan-to-value ratio of public sector
          banks as of March 2019
          <A href="https://www.rbi.org.in/scripts/BS_PressReleaseDisplay.aspx?prid=47578">
            was 69.9 percent.
          </A>
          For housing finance companies, this was at 72 percent. We can roughly
          say that financial institutions financed around 70 percent of the
          official price of the house by giving a home loan.
        </p>
        <p>
          This meant that the borrower had to bring in 30 percent as
          downpayment. On a loan of Rs 28.68 lakh — the average loan given by
          public sector banks and housing finance companies in 2018-19 — the
          downpayment works out to around Rs 12.29 lakh. Hence, the official
          price of the house works out to around Rs 41 lakh. If there is a black
          component involved, that is over and above this.
        </p>
        <p>
          It needs to be mentioned here that we are looking at data as of March
          2019. The latest data in this case isn’t available. But what is
          available is the RBI House Price Index which suggests that home prices
          went up by three percent between December 2018 and December 2019. We
          can assume the same house price increase between March 2019 and March
          2020.
        </p>

        <p>
          This means that the official price of such a house as of March 2020
          would have been around Rs 42.2 lakh (1.03 multiplied by Rs 41 lakh).
          The question is, will the Indian consumer buy this house in the
          post-Covid environment by taking on a home loan? se in the post-Covid
          environment by taking on a home loan?
        </p>
      </div>
      <div className="text-wrapper">
        <H4>Why have people gone slow on buying homes?</H4>
        <p>
          In an environment where even the FMCG sector is expected to contract,
          and car sales and two-wheeler sales have already gone down, it is very
          difficult to see people queuing up to buy homes to live in. The
          financial commitment required for this in an environment where jobs
          are being lost and incomes are going down is just too much. (Of
          course, the example I have used is an average, but the logic applies
          to the economy as a whole.)
        </p>
        <p>
          The trouble with the residential real estate sector in India is that
          there is very little agglomerated data going around, which would help
          us understand broad trends. But we can draw inferences from the data
          we have available.
        </p>
        <p>
          Let’s take a look at the growth in incremental home loans of banks
          over the years. In Table 1 and Table 2, we looked at home loans
          disbursed. Along with home loans being disbursed every year, they are
          also being repaid. Once we adjust for that repayment, what remains are
          the incremental home loans. In Figure 3, we plot the growth of
          incremental home loans for banks. Banks control around two-thirds of
          the home loan market.
        </p>
      </div>

      <CustomRangeLineChart
        xTooltipLabel={x => `Year: ${x}`}
        yTooltipLabel={y => `Per: ${y}%`}
        title="Figure 3: Growth of incremental home loans for banks."
        data={homeLoansGrowthFall}
        yLabel="Grown / Fall in %"
        sourceText="Source: Reserve Bank of India"
      />
      <div className="text-wrapper">
        So, as per Figure 3, the incremental home loans contracted by 3.6
        percent in 2019-20. This means that even before the Covid crisis struck,
        people were going slow on the purchase of housing. Hence, in 2020-21 the
        situation is only going to get worse.
        <p>
          There is another chart that suggests precisely this. Take a look at
          Figure 4.
        </p>
      </div>
      <CustomTestLineChart
        xTooltipLabel={x => `Month: ${x}`}
        yTooltipLabel={y => `Per: ${y}%`}
        title="Figure 4: Returns on housing as per the RBI’s House Price Index."
        yLabel="Grown / Fall in %"
        data={yearlyReturnsRBIHousePriceIndex}
        sourceText="Source: Reserve Bank of India"
      />
      <div className="text-wrapper">
        <p>
          Figure 4 plots the returns on housing as per the RBI’s House Price
          Index. These are all-India returns which are made up from returns from
          specific cities (Ahmedabad, Bengaluru, Chennai, Delhi, Jaipur, Kanpur,
          Kochi, Kolkata, Lucknow and Mumbai).
        </p>
        <p>
          It is entirely possible that the returns you made from real estate
          might have been more than what the chart shows — or even less, for
          that matter. Also, the chart plots one-year returns starting from
          December 2011, and it plots yearly returns every three months over the
          last nine years. This is a weakness in the chart, given that real
          estate started rallying post 2002 and actually rallied the most
          between 2002 and 2011.
        </p>
        <p>
          This is why the one-year all-India return in December 2011 stood at
          26.3 percent. In fact, returns in some cities were even higher. The
          one-year return in Delhi in December 2011 stood at 48.4 percent. In
          Bengaluru, the return stood at 41.6 percent. But what takes the cake
          is the one-year return in Kolkata in December 2012 and March 2013,
          which stood at a whopping 57.5 percent and 59.7 percent, respectively.
          Those were the days.
        </p>
        <p>
          In fact, as we shall see, such astonishingly high returns still have
          some role to play in the currently dysfunctional real estate market in
          India. While the specifics may vary across India, Figure 4 does show a
          broader trend. Housing price growth over the last nine years has
          slowed down big time. The all-India yearly return on housing in
          December 2019 stood at just 3.5 percent. A bank savings account could
          have possibly paid you more. The five-year return on housing works out
          to just 6.7 percent per year. Fixed deposits would have paid more with
          considerably less risk.
        </p>
        <p>
          Also, it needs to be pointed out here that the return on housing
          discussed above doesn’t really take into account the various expenses
          of owning a home. These include maintenance charges to the society,
          general maintenance charges, property tax, interest to be paid on the
          home loan and, above all, inflation.
        </p>
      </div>

      <div className="text-wrapper">
        <p>
          There are tax benefits of owning a house by financing it through a
          home loan. Also, one can rent a home out and earn money from it,
          though it needs to be said here that the rental yield (annual rent
          divided by the market price of the house) in most parts of the country
          is between 1.5-2 percent, which is very low.
        </p>
        <p>
          Once we take all these factors into account, the return on owning a
          house over the last few years may even be in negative territory. This
          explains why the so-called real estate investor is nowhere as active
          as he used to be a decade ago. And that, in turn, is also one of the
          reasons for the huge amount of unsold inventory of homes that builders
          have been stuck with over the years. There are multiple estimates of
          this, depending on which real estate consultant one wants to believe.
        </p>
        <p>
          <A href="https://www.financialexpress.com/money/affordable-homes-make-up-for-half-unsold-stock-in-9-indian-cities-report/1855920/">
            {" "}
            As per Proptiger.com
          </A>{" "}
          , the nine major residential markets in India had a total unsold
          inventory of 7.75 lakh units as of December 2019. Of this, nearly 3.90
          lakh units were in the affordable segment. Another estimate made by
          <A href="https://economictimes.indiatimes.com/wealth/real-estate/housing-sales-drop-29-in-jan-mar-unsold-stock-touches-rs-3-65-lakh-cr-amid-lockdown-jll/articleshow/75024768.cms?from=mdr">
            property consultant JLL suggests
          </A>
          that the number of unsold homes across the top seven property markets
          in the country was 4.55 lakh units as of March 2020. This unsold
          inventory was worth Rs 3.7 lakh crore.
        </p>
        <p>
          <A href="https://www.businesstoday.in/bt-buzz/news/bt-buzz-5-per-cent-of-india-gdp-is-stuck-in-unsold-housing-inventory/story/382957.html#:~:text=Over%2013%20lakh%20houses%20worth,in%20the%20last%20many%20decades.">
            An October 2019 report
          </A>{" "}
          by Liases Foras, a real estate research and consultancy firm, had
          pointed out that the total number of unsold homes across India was
          over 13 lakh units. These homes were supposed to be worth a huge Rs
          9.38 lakh crore.
        </p>
        <p>
          Over and above this, there is a huge number of homes that have been
          bought as an investment over the years and are lying locked, because
          the landlords are not comfortable with the risk of renting these homes
          out. In fact, Anshuman Magazine of the property consultancy firm CBRE
          had said
          <A href="https://www.bbc.com/news/world-asia-india-32644293">
            five years back
          </A>
          that "around 12 million completed houses" were "lying vacant across
          urban India".
        </p>
        <p>
          So, the total number of unused homes — homes that have been built, but
          with no one living in them — is pretty huge. And a home in which no
          one lives is largely a waste.
        </p>

        <p>
          What this clearly tells us is that an astonishingly high amount of
          money is stuck in real estate, which is not a good thing in a capital
          deficient country like India. Also, unless these homes get sold, loans
          given by banks and NBFCs will continue to remain stuck.
        </p>
        <p>
          Other than the real estate investors disappearing, the major reason
          for such a huge amount of unsold homes is the price of these homes. It
          is beyond what most people who actually want a home to live in can
          afford.
        </p>

        <p>
          Between December 2010 and December 2019, home prices across India rose
          by 12.1 percent per year. But as mentioned earlier, this does not take
          into account the massive increase in home prices before that, between
          2002 and 2011. In fact, prices in Delhi, Mumbai and other cities rose
          by even more than 25 percent in a year in many of these years. This
          growth continued well into 2015. The one-year housing return in March
          2015 stood at 17.5 percent.
        </p>
        <p>
          The point being that even though the increase in housing price has
          slowed down since 2015, the base price of homes was already very high
          by then, making homes unaffordable for most people looking to buy one
          to live in.
        </p>
        <p>
          And that explains such a massive rise in the inventory of unsold
          homes.
        </p>
      </div>

      <div className="text-wrapper">
        <H4>Why don’t home prices fall?</H4>
        <p>
          Home prices need to come down for homes to become affordable. Even the
          so-called affordable homes aren’t really affordable. If they were,
          they wouldn’t have seen such a massive pile up of inventory, as has
          been the case.
        </p>
        <p>
          The question is, why don’t home prices fall? The answer to this is a
          rather complicated one and there are several reasons for the same.
          Let’s look at this in detail.
        </p>
      </div>
      <Image5 alt="India's real estate - factory artwork"></Image5>
      <div className="text-wrapper">
        <p>
          Take a look at Figure 5. It basically plots the total amount of money
          lent by banks and non-banking finance companies (NBFCs, including
          housing finance companies) to real estate companies.
        </p>
      </div>
      <FigureText text="Figure 5: Total amount of money lent to real estate companies by banks and NBFCs." />
      <div className="flex justify-center my-8">
        <Figure5 />
      </div>
      <div className="text-wrapper">
        <Source text="Source: India’s Great Slowdown: What Happened? What’s the Way Out? by Arvind Subramanian and Josh Felman; Centre for International Development; Harvard University" />
      </div>
      <div className="h-5" />
      <div className="text-wrapper">
        <p>
          It is interesting to see that the total amount of money lent to real
          estate companies by banks and NBFCs has gone up dramatically over the
          years, with a bulk of this increase coming after 2016.
        </p>
        <p>
          The overall bank lending to real estate companies increased from Rs
          1.78 lakh crore as of March 2016 to Rs 2.3 lakh crore as of March
          2020. This means an increase of 6.6 percent per year. This was much
          slower than the increase in non-food credit by economic activity,
          which was at 8.9 percent per year during the same period.
        </p>
        <p>
          The Food Corporation of India and other state procurement agencies buy
          rice and wheat directly from the farmers across large parts of the
          country at a minimum support price decided by the government. Banks
          finance this purchase. This is referred to as food credit. Once this
          is deducted from the total loans given by banks, what remains is
          non-food credit.
        </p>
        <p>
          So, the bank credit to the real estate sector has grown at a fairly
          conservative pace over the last few years. In fact, a bulk of the
          growth in bank lending to real estate came between 2007-08 and
          2012-13, when the overall lending grew from Rs 63,168 crore to Rs 1.26
          lakh crore, at a rate of 14.8 percent per year.
        </p>
        <p>
          What this means is that the lending by NBFCs to real estate companies
          has grown rapidly in the last few years. Why has that happened? Take a
          look at Figure 6, which basically plots the total amount of lending
          carried out by banks to NBFCs.
        </p>
      </div>
      <CustomTestLineChart
        xTooltipLabel={x => `Month: ${x}`}
        yTooltipLabel={y => `₹ ${y}`}
        title="Figure 6: Total amount of lending by banks to NBFCs."
        data={nonBankingFinCompanies}
        sourceText="Source: Reserve Bank of India"
      />

      <div className="text-wrapper">
        <p>
          As can be seen from Figure 6, the lending by banks to NBFCs started
          going up only post December 2016. Why is that the case? In November
          2016, the government demonetised notes of Rs 500 and Rs 1,000. These
          notes had to be deposited with banks. Banks suddenly saw a huge inflow
          of deposits, without a similar increase in loans. Banks then started
          increasing their lending to NBFCs which, in turn, lent a lot of this
          incoming money to real estate companies.
        </p>
        <p>
          How is all this related to real estate prices not falling? The
          business model of real estate funding has changed over the years. In
          the past, real estate companies would launch projects and sell
          under-construction property. This money would then be used to either
          buy land or complete a previous project or simply be siphoned off.
          Another project would be launched to raise money in order to build or
          complete a project that had been announced in the past.
        </p>
        <p>
          If I were to talk like an analyst, real estate companies were in a
          negative working capital business. They didn’t have to put their own
          money into the business upfront. This model broke down once many
          builders became too cavalier in their approach and did not deliver
          apartments on time. This started happening around 2009 and peaked a
          few years later. Other than genuine buyers, investors also got caught
          in this scam (for the lack of a better word).
        </p>
        <p>
          Hence, the model of launching a project and funding a previous project
          basically broke down. Gradually, under-construction property was
          looked down upon and real estate companies had to start depending on
          more formal sources of finance. Their very own little Ponzi scheme,
          where they raised money from a new project to finance a past project,
          basically broke down.
        </p>
        <p>
          Also, after letting the sector run riot for many years, the government
          finally decided to regulate it. With the passage of the Real Estate
          (Regulation and Development) Act, 2016, real estate companies need to
          have all approvals in place before the launch, unlike previously,
          where they used to launch projects with barely any preparation. Hence,
          real estate companies need to have all the approvals in place before
          they start selling the project. This means they need to invest money
          upfront.
        </p>
        <p>
          As Arvind Subramanian and Josh Felman write in a paper titled
          <span className="italic">
            India’s Great Slowdown: What Happened? What’s the Way Out?{" "}
          </span>
          : “Historically the bulk of formal sector real estate funding was
          provided by banks, but in recent years most of the incremental lending
          has come from NBFCs, so much so that by 2018-19, NBFCs accounted for
          about half of the Rs 5 lakh crore in real estate loans outstanding.
          This funding was provided on the assumption that developers would be
          able to complete their projects, sell off their inventories, and then
          repay.”
        </p>
        <p>
          But as we know inventories have only gone up over the last few years.
          This basically means that builders already had a lot of unsold homes.
          On top of that, they built more homes and sold fewer homes than the
          new homes they have built. This led to the inventory going up.
        </p>
      </div>

      <div className="text-wrapper">
        <p>
          As Subramanian and Felman write: “In terms of the number of units,
          unsold inventory in the top eight cities has risen to nearly 10 lakhs
          at end-June 2019, compared to annual sales of just over two lakh
          units. Translated into rupees, unsold inventory amounts to Rs 8 lakh
          crores, equivalent to around four years’ worth of sales.”
        </p>
        <p>
          As we have seen from data provided earlier, the inventory situation
          has only become worse since June 2019. So, why haven’t real estate
          companies cut prices and brought down their inventory? As Subramanian
          and Felman point out: “While developers could in principle tempt
          buyers into the market by reducing prices, they couldn’t do this in
          practice because lower prices would have destroyed the (notional)
          value of the collateral that they had pledged in order to secure their
          lending.”
        </p>
        <p>
          When builders borrowed money from banks and NBFCs, they had to provide
          collateral for the same. The collateral came in the form of the homes
          they had built and not sold. In that scenario, if they cut prices and
          sold homes, the value of the collateral would fall. This would create
          a problem both for real estate companies and banks. This is why they
          ended up with a huge unsold inventory along with unreal prices which
          most genuine buyers cannot afford.
        </p>
        <p>
          What about individual buyers who bought flats over the years as an
          investment and continue to hold on to it, unwilling to sell it at a
          lower price? Buyers who bought flats late in the day, between 2012 and
          2015, paid a high price for what they had bought. After factoring in
          the costs of owning real estate, they may feel they have barely made
          any money from their investment.
        </p>
        <p>
          They are holding on to their investment in the hope and the belief
          that someday, the good days of the past will return, and they will
          make good money from their investment. Meanwhile, they seem to be
          ready to hold on to what is basically a losing investment.
        </p>
        <p>
          In some other cases, buyers are anchored on to the prices they saw
          before 2015. While, on the whole, the Indian real estate market has
          generated positive though minimal returns even after 2015, there are
          parts of the country where real estate prices have gone down.
        </p>
        <p>
          Meanwhile, this has created a situation which is rarely seen in any
          economic system. There is{" "}
          <span className="italic">low demand for real estate</span> (given the
          high price) and there is low supply as well (given that real estate
          companies and individual owners are unwilling to cut prices). I may
          want to buy a home but unless I have enough money and the ability to
          borrow to do so, I am really not adding to demand. Just wanting
          something, without having the money to finance it, doesn’t really add
          to demand.
        </p>
        <p>
          There are other reasons also as to why transactions are not happening.
        </p>
        <p>
          In some real estate markets, the market price is lower than the
          prevailing circle rate. What does this mean? State governments decide
          on a certain circle rate for different areas. If a real estate
          transaction happens, then stamp duty needs to be paid on this rate,
          irrespective of the prevailing market price. When the market price is
          higher than the circle rate, there are no problems.
        </p>
        <p>
          But when the market price goes below the circle rate, it creates
          multiple problems. Let’s say the market price of a particular
          apartment in a city is Rs 50 lakh. The circle rate is Rs 60 lakh. If
          this apartment is sold, the stamp duty will have to be paid at the
          circle rate of Rs 60 lakh and not the market price of Rs 50 lakh.
        </p>

        <p>
          Also, Rs 10 lakh — the difference between the circle rate and the
          market price — will be added to the buyer’s income and he will have to
          pay income tax on it. On the seller’s side, the capital gains will be
          calculated using a selling price of Rs 60 lakh and not Rs 50 lakh.
          This is done to discourage black transactions.
        </p>
        <p>
          This basically ensures that real estate transactions in such markets
          don’t happen at all. This means that the inventory with real estate
          companies does not come down. Along with this, the individual buyers
          also cannot sell the apartments they own. There is a clear need on the
          part of the state governments to ensure that the circle rates are at
          realistic levels.
        </p>
      </div>
      <Image1 alt="India's real estate - house artwork" />
      <div className="text-wrapper">
        <p>
          The question is, why don’t state governments decrease circle rates to
          realistic levels? One reason for this can possibly lie in the fact
          that most of the ill-gotten wealth of state-level politicians is in
          real estate. In fact, many real estate companies, both small and big,
          are essentially fronts for politicians. A decrease in circle rates
          would mean that transactions will start happening at a lower price.
          This is something that politicians don’t want because it will mean a
          decrease in their overall wealth. They would rather have no
          transactions than have transactions at a lower price. Do remember, all
          these investments are in black.
        </p>
        <p>
          Then there is also the case of stamp duty on real estate transactions
          forming a significant portion of the revenue of state governments.
          While at lower stamp duty rates, the chances of more real estate
          transactions are higher, this is a risk that state governments don’t
          seem to be willing to take. Take a look at Table 3, which lists out
          the stamp duty collections of a few states.
        </p>
      </div>
      <FigureText text="Table 3: Stamp duty collections of some states. (In Rs crore, rounded off to the nearest crore.)" />
      <Table
        titles={[
          "State",
          "2018-19 Stamp Duty Collections",
          "2018-19 Total Revenue",
          "2019-20 Stamp Duty Revised Estimates",
          "2019-20 Total Revenue Revised Estimates",
        ]}
        rows={[
          [
            <A href="https://finance.delhigovt.nic.in/sites/default/files/All-PDF/AFS%202020-21.pdf">
              Delhi{" "}
            </A>,
            "4,459",
            "43,113",
            "4,900",
            "51,562",
          ],
          [
            <A href="https://www.prsindia.org/parliamenttrack/budgets/maharashtra-budget-analysis-2020-21">
              Maharashtra
            </A>,
            "28,545",
            "3,08,154",
            "29,500",
            "3,87,102",
          ],
          [
            <A href="https://financedepartment.gujarat.gov.in/Documents/Bud-Guj_997_2020-2-26_46.pdf">
              Gujarat
            </A>,
            "7,781",
            "1,36,002",
            "8,500",
            "1,49,827",
          ],
          [
            <A href="http://www.tnbudget.tn.gov.in/tnweb_files/demands/61%20Annual_Financial_statements.pdf">
              Tamil Nadu
            </A>,
            "11,066",
            "1,73,741",
            "13,122",
            "1,91,860",
          ],
          [
            <A href="http://web1.hry.nic.in/budget/fsmemofinal.pdf">Haryana</A>,
            "5,636",
            "65,885",
            "6,600",
            "77,581",
          ],
        ]}
      />
      <Source
        text={
          "Source: In the hyperlinks. (I would like to thank Abhyudaya Tyagi for putting together the data for this table)."
        }
      />
      <div className="text-wrapper">
        <H4>Will home prices fall in the post-Covid world?</H4>
        <p>
          What we have discussed up until now are reasons from the past. The
          question is, how will things evolve in a post-Covid world? Will real
          estate prices fall?
        </p>
        <p>
          Let me put it this way, before I get into the details: the deep state
          of Indian real estate that prevails (the real estate companies, the
          real estate consultancies, the governments, the banks, the investors
          and the NBFCs) will try their best to ensure that real estate prices
          do not fall.
        </p>
        <p>
          The mathematics of the entire business works out such that builders
          currently cannot cut prices by more than 5-10 percent. Analyst Adhidev
          Chattopadhyay pointed this out in a recent report he wrote for ICICI
          Securities. The logic for central and south Mumbai, where the
          country's most expensive real estate is sold, is slightly different.
        </p>
        <p>What did Chattopadhyay say? Let’s look at it pointwise.</p>
        <ul className="list-decimal">
          <li>
            The gross margins that real estate companies work with are around
            15-20 percent. This is for non-Mumbai real estate companies
            operating in the biggest cities in the country. The average selling
            price is Rs 5,000-6,000 per square foot and has been stagnant post
            demonetisation. If we take construction costs, approval costs, land
            costs and marketing costs, this works out in the range of Rs
            4,300-4,900 per square foot. What this implies is that there is a
            very limited scope for real estate companies to cut prices in order
            to make their homes attractive enough for prospective buyers.
          </li>
          <li>
            Unlike manufacturing companies, the cost of building homes varies
            across different parts of the country. Also, money in building homes
            is spent over a period of four to five years; hence, costs cannot
            suddenly be brought down. In this scenario, it will be difficult for
            real estate companies to cut prices beyond 5-10 percent (again
            non-Mumbai). If they go beyond that, they will need to make
            loss-making sales and no one wants to sell at a loss. This means
            stagnation in the market and the low supply, low demand scenario
            that I had talked about earlier.
          </li>
          <li>
            <p>
              Chattopadhyay also feels that these price cuts will be more along
              the lines of indirect discounts from real estate companies, rather
              than a direct cut in the market price. The reason for this is very
              straightforward. No real estate company is able to sell all the
              homes that it builds in a particular project all at once. The
              initial sales happen at a certain price. If the real estate
              company cuts prices in the days to come, this does not go down
              well with the individuals who had bought these homes at the
              beginning. Hence, the pricing remains opaque and discounts need to
              be negotiated.
            </p>
            <p>
              Over the long term, this has been a major reason why real estate
              in India has become sluggish. There is nothing like a market
              price. On the flip side, what this tells any prospective buyer is
              to negotiate hard. It might just be your lucky day.
            </p>
          </li>
          <li>
            In the days to come, labour costs are unlikely to come down. Many
            construction workers have gone back to their homes in states like
            Bihar, Uttar Pradesh, Jharkhand, Odisha, Chhattisgarh, etc. Given
            this, there will be a shortage of construction workers. Hence, the
            wages paid to labourers will go up, driving up the cost along the
            way.
          </li>
          <li>
            <p>
              Over the years, a lot of foreign money has come into Indian real
              estate (particularly commercial real estate). Analysts who track
              real estate expect the money coming in through this route to slow
              down in the time to come. Most of this money came in from Western
              economies. And now that these economies are in a recession, the
              money coming in is bound to slow down.
            </p>
            <p>
              The problem with this argument is that an equally convincing and
              opposite argument can be made. Take the case of the United States.
              Between February 26 and June 10, the Federal Reserve, the American
              central bank, printed a little over $3 trillion. Other Western
              economies are also printing money. This money can easily flow to
              any part of the world. Of course, whether it does remains to be
              seen.
            </p>
          </li>
          <li>
            <p>
              Kunal Lakhan, an analyst at CLSA, makes a very interesting point
              regarding all the inventory that has piled up in central Mumbai
              (the areas of Dadar, Prabhadevi, Worli, Lower Parel, Parel, etc.).
              Lakhan says that there are 3,000 apartments in this area, with an
              average price of Rs 10 crore. The question is, who is going to buy
              these homes? There are 3,780 multi-millionaires in Mumbai. Most of
              these individuals already have homes in south Mumbai (which has
              the country’s most expensive real estate). So, how many more homes
              can they buy?
            </p>
            <p>
              Lakhan estimates that some of the key projects in central Mumbai
              have seen a fall in price of 25-30 percent within one or two years
              of getting the occupation certificate. He estimates that prices
              might fall a further 12-15 percent in the post-Covid environment
              in case of some projects. The inventory piled up could take up to
              a decade to clear. There is a ghost city right in the middle of
              Mumbai. It’s just that it can’t be seen (like Greater Noida)
              because it’s all up in the air.
            </p>
            <p>
              So, when it comes to central Mumbai, the market is at work and the
              home prices have taken some beating. But the average price of the
              apartment is still so high that demand has totally collapsed. As I
              said earlier, if you are the kind who can afford such an
              apartment, you probably already have one, but if you still want to
              buy another one, negotiate very hard.
            </p>
          </li>
        </ul>

        <H4>The new new real estate story</H4>
        <p>
          The above reasons are genuine. But the fact of the matter is that for
          any market to work, the price at which consumers are willing to buy
          should match the price at which suppliers are willing to sell. In the
          case of real estate, whatever the companies and their lobbies might
          say, transactions are not going to happen unless home prices come
          down.
        </p>
        <p>
          Real estate consultancies and many analysts who follow the sector have
          now started talking about the economic environment improving by
          October and the demand coming back.
        </p>
        <p>
          There is nothing new here. This is something that has been happening
          for the last five years wherein sometime during the middle of the
          year, those who make their money from real estate start talking about
          a festival season boom in the sector. The boom hasn’t come, at least
          not in the last five years.
        </p>
        <p>
          Why should this year be any different? If things did not improve
          during the years when economic growth actually happened, how can they
          improve during a year when the economy is expected to contract? No
          real estate analyst worth his salt is willing to answer this question.
        </p>
        <p>
          If homes weren’t selling before Covid-19 struck, how are they going to
          sell in the post-Covid economic scenario, where incomes are likely to
          fall/stagnate, jobs will be lost, and businesses will be destroyed?
          This is another question that nobody is currently willing to answer.
        </p>
      </div>
      <Image6 caption={""} alt="India's real estate - VR artwork" />
      <div className="text-wrapper">
        <p>
          The real estate industry is trying to sell a positive story all over
          again. One strand of this story is builders going digital in order to
          beat the post-Covid economic heat. This essentially involves the real
          estate company arranging a virtual visit of the home for a prospective
          buyer. They are also setting up virtual meets for the prospective
          buyer with banks and home finance companies for a home loan.
        </p>
        <p>
          While it’s fantastic that companies are resorting to technology in
          order to boost their sales, I am really not sure about how many people
          are going to buy a home without going out there and seeing it for
          themselves. This might help a few big builders with some reputation to
          sell a few units here and there; nevertheless, it’s not going to help
          in clearing the massive inventory in any meaningful sort of way.{" "}
        </p>
      </div>

      <div className="text-wrapper">
        <p>
          The other story being sold is that people who are losing their jobs in
          the Middle East will come back and buy homes to live in. There is a
          big hole in this argument. A bulk of these people already own homes in
          India. As a recent
          <A href="https://www.livemint.com/news/india/expats-to-return-en-masse-in-charter-flights-kerala-throws-in-a-rider-11592141864667.html">
            report in <span className="italic">Mint</span>
          </A>
          points out: “Every fifth house in Kerala belongs to a person who works
          in the Gulf.” So, yes, some of these people may come back and buy
          homes, but again, this isn’t going to lead to any significant
          reduction in the inventory.
        </p>
        <H4>The big question</H4>
        <p>
          How will the real estate companies repay loans they have taken on from
          banks and NBFCs, if they don’t get around to selling the unsold
          inventory of homes?
        </p>
        <p>
          As I had said earlier, the system will come to their rescue and it
          has. In April earlier this year, the RBI came to the rescue of NBFCs
          by allowing them to extend by a year the date for starting operations
          for loans to real estate projects delayed for reasons beyond the
          control of promoters. This extension won’t be treated as restructuring
          of the loans given to real estate companies. This facility was already
          available to banks.
        </p>
        <p>
          If this extension was treated as restructuring, the banks as well as
          NBFCs would have to make provisions (that is, set aside money) for a
          possible default on these loans. And any provision would reduce
          profit.
        </p>
        <p>
          This move by the RBI allows real estate companies another year to meet
          their loan obligations. This helps banks as well as NBFCs in the short
          term, as the chances of real estate companies defaulting on loans are
          high, given the lack of new home sales.
        </p>
        <p>
          As mentioned earlier, quite a lot of what looks like lending to NBFCs
          on the books of banks is actually indirect lending to the real estate
          sector. In this scenario, if real estate companies don’t pay up NBFCs,
          the latter won’t be able to repay banks. Hence, an NBFC crisis might
          turn into a banking crisis, something the RBI would like to avoid.
        </p>
        <p>
          The moratorium on repaying principal as well as interest on term-loans
          is also going to help real estate companies in the short-term. But it
          is worth remembering that interest is not being waived off on these
          loans. At the end of the moratorium period, the interest for a period
          of six months (or three months, depending on the moratorium taken by
          the real estate company) will be added to the principal outstanding
          and that will have to be repaid. There is no free lunch available
          anywhere.
        </p>
        <p>
          What this basically does is, it pushes the can down the road. The
          question is, what will happen one year later? Of course, the hope is
          that demand will come back, real estate companies will be able to sell
          the inventory they have built up, and use the money to repay loans. As
          far as hopes go, even cows might fly one day.
        </p>
        <H4>Conclusion</H4>
        <p>
          If we continue the way we currently are, the real estate sector will
          continue to stagnate, at least for the next five years, as it has for
          the last five years. Other than being a huge factor in improving the
          quality of life that comes with owning a house (children don’t need to
          change schools, parents are happy, and so on), the real estate sector
          also has huge forward and backward linkages with 250 ancillary
          industries.
        </p>
        <p>
          As Keith Wardrip, Laura Williams and Suzanne Hague write in a research
          paper titled
          <span className="italic">
            {" "}
            The Role of Affordable Housing in Creating Jobs and Stimulating
            Local Economic Development: A Review of the Literature:{" "}
          </span>{" "}
          “During the construction of affordable housing — or any kind of
          housing, for that matter — the local economy benefits directly from
          the funds spent on materials, labour, and the like. If the builder is
          purchasing windows and doors from a local supplier, the supplier may
          have to spend money on materials and hire additional help to complete
          the order — examples of indirect effects. Finally, the construction
          workers, glass cutters and landscapers are likely to spend a portion
          of their wages at the local grocery store or shopping mall, which
          illustrates induced effects.”
        </p>
      </div>
      <Image7 caption={""} alt="India's real estate - rollercaster artwork" />
      <div className="text-wrapper">
        <p>
          This basically means that when the real estate sector does well, many
          other sectors — right from steel and cement to furnishing and paints,
          etc — do well. The multiplier effect is huge. And the multiplier
          effect can benefit India currently to tackle the negative impact of
          Covid and in the years to come.
        </p>
        <p>
          But for that to happen, home prices need to fall. Hence, it’s time the
          government and the RBI stop mollycoddling the real estate companies.
          After the current set of regulatory changes expire, the ones made to
          help real estate companies when it comes to repaying money they owe to
          banks and NBFCs, the government and the RBI shouldn’t offer any more
          help.
        </p>
        <p>
          Banks and the NBFCs should be allowed to demand repayments and if a
          real estate company defaults, then the collateral needs to be seized
          and sold. Real estate companies defaulting on loans need to be put
          through the Insolvency and Bankruptcy Code.
        </p>
        <p>
          Of course, this will mean some trouble for banks as well as NBFCs. But
          they will have to face this trouble sooner or later, given that if the
          unsold inventory of homes doesn’t get sold, there is no way real
          estate companies are in a position to repay the loans they have taken
          on.
        </p>
        <p>
          In fact, Lakhan of CLSA estimates that the annual debt repayments of
          the sector are more than twice its disposable income. How is the debt
          ever going to be repaid if the inventory doesn’t sell?
        </p>
        <p>
          Also, only once the prices fall, the demand is going to come back, and
          only then the real estate sector can hope to get back on to its feet.
        </p>
        <p>
          As Nassim Nicholas Taleb writes in a similar vein in
          <span className="italic"> Antifragile </span> in the context of the
          financial crisis of 2008 and Alan Greenspan, the chairman of the US
          Federal Reserve between 1988 and 2006: “A main source of the economic
          crisis that started in 2007 lies in the...attempt by...Alan
          Greenspan...to iron out the ‘boom-bust cycle’ which caused risks to go
          hide under the carpet and accumulate there until they blew up the
          economy.”
        </p>
        <p>
          The more a government or a central bank tries to postpone a crisis,
          the bigger the eventual crisis is. This is something that the RBI and
          the government need to realise.
        </p>
        <div className="my-10">
          <i className="italic">Vivek Kaul is the author of Bad Money.</i>
        </div>
        <hr />
        <div className="my-10">
          <div className="font-bold">
            <div className="mb-5">Credits</div>
          </div>
          <div className="my-3">
            <div className="mb-2">
              <span className="font-bold">Artwork - </span> Anish Daolagupu
            </div>
            <div className="mb-2">
              <span className="font-bold">Design - </span> Nishan Bende
            </div>
            <div className="mb-2">
              <span className="font-bold">Editing - </span> Jayashree
              Arunachalam
            </div>
          </div>
        </div>
        ***
        <div className="italic">
          This story is part of the NL Sena project, which 27 of our readers
          contributed to. It was made possible thanks to Vikhyath Kamath and
          other NL Sena members. Contribute to our{" "}
          <A href="https://www.newslaundry.com/sena">next NL Sena project</A>,
          India’s Custodial Deaths, and help to keep news free and independent.
        </div>
        <div className="flex w-full justify-end mt-5">
          <ShareButtons />
        </div>
      </div>
      <MeTypeComponent />
      <SubscribeBottom />
    </div>
  )
}

export default IndexPage
